/**
 * Утилиты для работы с темами приглашений
 */

import { VALID_STANDARD_TEMPLATES } from '@/constants/templates';
import { logger } from './logger';
// Стандартные темы
import classicStyles from '@/themes/standard/classic.module.css';
import modernStyles from '@/themes/standard/modern.module.css';
import romanticStyles from '@/themes/standard/romantic.module.css';
import vintageStyles from '@/themes/standard/vintage.module.css';
import minimalStyles from '@/themes/standard/minimal.module.css';
import gardenStyles from '@/themes/standard/garden.module.css';

/**
 * Типы стилей для стандартных тем (skins)
 * Используется только для стандартных тем, премиум темы имеют свои layouts
 */
type ThemeStyles = {
  container: string;
  content: string;
  decorativeTop: string;
  decorativeBottom: string;
  title: string;
  and: string;
  infoItem?: string;
  name?: string;
  infoLabel?: string;
  infoValue?: string;
  invitationText?: string;
  checkmark: string;
  successMessageTitle?: string;
  successMessageText?: string;
  loading?: string;
  error?: string;
  errorTitle?: string;
};

type StandardTemplateValue = typeof VALID_STANDARD_TEMPLATES[number];

/**
 * Маппинг стандартных шаблонов на CSS модули
 *
 * Важно: Явно извлекаем все свойства из CSS модулей,
 * чтобы они не были удалены при tree-shaking в production
 *
 * КРИТИЧНО: Все классы должны быть явно извлечены,
 * иначе они будут удалены при сборке
 *
 * Премиум темы НЕ используют этот маппинг - у них свои layouts
 */
const THEME_STYLES_MAP: Record<StandardTemplateValue, ThemeStyles> = {
  classic: {
    container: classicStyles.container,
    content: classicStyles.content,
    decorativeTop: classicStyles.decorativeTop,
    decorativeBottom: classicStyles.decorativeBottom,
    title: classicStyles.title,
    and: classicStyles.and,
    infoItem: classicStyles.infoItem,
    name: classicStyles.name,
    infoLabel: classicStyles.infoLabel,
    infoValue: classicStyles.infoValue,
    invitationText: classicStyles.invitationText,
    checkmark: classicStyles.checkmark,
    successMessageTitle: classicStyles.successMessageTitle,
    successMessageText: classicStyles.successMessageText,
    loading: classicStyles.loading,
    error: classicStyles.error,
    errorTitle: classicStyles.errorTitle,
  },
  modern: {
    container: modernStyles.container,
    content: modernStyles.content,
    decorativeTop: modernStyles.decorativeTop,
    decorativeBottom: modernStyles.decorativeBottom,
    title: modernStyles.title,
    and: modernStyles.and,
    infoItem: modernStyles.infoItem,
    name: modernStyles.name,
    infoLabel: modernStyles.infoLabel,
    infoValue: modernStyles.infoValue,
    invitationText: modernStyles.invitationText,
    checkmark: modernStyles.checkmark,
    successMessageTitle: modernStyles.successMessageTitle,
    successMessageText: modernStyles.successMessageText,
    loading: modernStyles.loading,
    error: modernStyles.error,
    errorTitle: modernStyles.errorTitle,
  },
  romantic: {
    container: romanticStyles.container,
    content: romanticStyles.content,
    decorativeTop: romanticStyles.decorativeTop,
    decorativeBottom: romanticStyles.decorativeBottom,
    title: romanticStyles.title,
    and: romanticStyles.and,
    infoItem: romanticStyles.infoItem,
    name: romanticStyles.name,
    infoLabel: romanticStyles.infoLabel,
    infoValue: romanticStyles.infoValue,
    invitationText: romanticStyles.invitationText,
    checkmark: romanticStyles.checkmark,
    successMessageTitle: romanticStyles.successMessageTitle,
    successMessageText: romanticStyles.successMessageText,
    loading: romanticStyles.loading,
    error: romanticStyles.error,
    errorTitle: romanticStyles.errorTitle,
  },
  vintage: {
    container: vintageStyles.container,
    content: vintageStyles.content,
    decorativeTop: vintageStyles.decorativeTop,
    decorativeBottom: vintageStyles.decorativeBottom,
    title: vintageStyles.title,
    and: vintageStyles.and,
    infoItem: vintageStyles.infoItem,
    name: vintageStyles.name,
    infoLabel: vintageStyles.infoLabel,
    infoValue: vintageStyles.infoValue,
    invitationText: vintageStyles.invitationText,
    checkmark: vintageStyles.checkmark,
    successMessageTitle: vintageStyles.successMessageTitle,
    successMessageText: vintageStyles.successMessageText,
    loading: vintageStyles.loading,
    error: vintageStyles.error,
    errorTitle: vintageStyles.errorTitle,
  },
  minimal: {
    container: minimalStyles.container,
    content: minimalStyles.content,
    decorativeTop: minimalStyles.decorativeTop,
    decorativeBottom: minimalStyles.decorativeBottom,
    title: minimalStyles.title,
    and: minimalStyles.and,
    infoItem: minimalStyles.infoItem,
    name: minimalStyles.name,
    infoLabel: minimalStyles.infoLabel,
    infoValue: minimalStyles.infoValue,
    invitationText: minimalStyles.invitationText,
    checkmark: minimalStyles.checkmark,
    successMessageTitle: minimalStyles.successMessageTitle,
    successMessageText: minimalStyles.successMessageText,
    loading: minimalStyles.loading,
    error: minimalStyles.error,
    errorTitle: minimalStyles.errorTitle,
  },
  garden: {
    container: gardenStyles.container,
    content: gardenStyles.content,
    decorativeTop: gardenStyles.decorativeTop,
    decorativeBottom: gardenStyles.decorativeBottom,
    title: gardenStyles.title,
    and: gardenStyles.and,
    infoItem: gardenStyles.infoItem,
    name: gardenStyles.name,
    infoLabel: gardenStyles.infoLabel,
    infoValue: gardenStyles.infoValue,
    invitationText: gardenStyles.invitationText,
    checkmark: gardenStyles.checkmark,
    successMessageTitle: gardenStyles.successMessageTitle,
    successMessageText: gardenStyles.successMessageText,
    loading: gardenStyles.loading,
    error: gardenStyles.error,
    errorTitle: gardenStyles.errorTitle,
  },
};

/**
 * Получить стили для указанной стандартной темы
 *
 * Используется только для стандартных тем (skins).
 * Премиум темы используют собственные layouts и не используют эту функцию.
 *
 * @param template - Значение стандартного шаблона
 * @returns CSS модуль с классами темы
 */
export const getThemeStyles = (template?: string): ThemeStyles => {
  const normalizedTemplate = (template?.trim().toLowerCase() || 'classic') as StandardTemplateValue;

  if (!(normalizedTemplate in THEME_STYLES_MAP)) {
    logger.warn('[getThemeStyles] Неизвестная стандартная тема, используется classic:', {
      template,
      normalizedTemplate,
      available: Object.keys(THEME_STYLES_MAP),
    });
    return THEME_STYLES_MAP.classic;
  }

  const selectedTheme = THEME_STYLES_MAP[normalizedTemplate];

  // Проверяем, что классы не пустые (защита от tree-shaking)
  if (!selectedTheme.container || !selectedTheme.content) {
    logger.error('[getThemeStyles] Классы темы пустые!', {
      template,
      normalizedTemplate,
      container: selectedTheme.container,
      content: selectedTheme.content,
      containerType: typeof selectedTheme.container,
      contentType: typeof selectedTheme.content,
      allStyles: selectedTheme,
      // Проверяем исходные CSS модули
      classicStyles: classicStyles.container,
      modernStyles: modernStyles.container,
      romanticStyles: romanticStyles.container,
      vintageStyles: vintageStyles.container,
      minimalStyles: minimalStyles.container,
      gardenStyles: gardenStyles.container,
    });
    // Fallback на classic, если классы пустые
    return THEME_STYLES_MAP.classic;
  }

  return selectedTheme;
};

/**
 * Получить класс для контейнера темы
 *
 * @param template - Значение шаблона
 * @returns CSS класс для контейнера
 */
export const getThemeContainerClass = (template?: string): string => {
  const styles = getThemeStyles(template);
  return styles.container;
};

/**
 * Получить класс для контента темы
 *
 * @param template - Значение шаблона
 * @returns CSS класс для контента
 */
export const getThemeContentClass = (template?: string): string => {
  const styles = getThemeStyles(template);
  return styles.content;
};
